import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box, Button,
  CircularProgress,
  Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle,
  FormControl, Grid,
  IconButton,
  InputLabel,
  List, ListItem,
  MenuItem, Paper, Select,
  Snackbar,
  Tab, Tabs, TextField,
  Tooltip,
  Typography,
  LinearProgress,
  Checkbox,
  FormControlLabel,
  Collapse,
  Stack,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import DocViewer, { DocViewerRenderers } from 'react-doc-viewer';
import { useLocation, useParams } from 'react-router-dom';
import * as XLSX from 'xlsx'; // Importing SheetJS for Excel files
import AccountsTab from '../sections/@dashboard/matter/AccountsTab';
import AppointmentsTab from '../sections/@dashboard/matter/appointmentsTab';
import CashbookTransactionsTab from '../sections/@dashboard/matter/CashbookTransactionsTab';
import AssetsTab from '../sections/@dashboard/matter/AssetsTab';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import InputAdornment from '@mui/material/InputAdornment';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

// Function to render different types of document previews
const renderDocumentPreview = (file) => {
  const fileExtension = file.filename.split('.').pop().toLowerCase();
  console.log('File:', file.filename, 'Extension:', fileExtension); // Debugging line

  // Check for file extension and render appropriate preview
  if (fileExtension === 'pdf') {
    return (
      <Box sx={{ width: '100%', height: '60vh' }}>
        <embed
          src={`https://ipms.insollex.com/api/uploads/${file.filename}`}
          width="100%"
          height="100%"
          type="application/pdf"
        />
      </Box>
    );
  } else if (['jpg', 'jpeg', 'png'].includes(fileExtension)) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '40vh', // fixed height instead of maxHeight
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center', // center vertically too
        }}
      >
        <img
          src={`https://ipms.insollex.com/api/uploads/${file.filename}`}
          alt="Document preview"
          style={{
            maxHeight: '100%',
            maxWidth: '100%',
            objectFit: 'contain',
            borderRadius: 8,
          }}
        />
      </Box>
    );
  } else if (fileExtension === 'txt') {
    return <TextFileViewer file={file} />;
  } else if (fileExtension === 'docx') {
    const docs = [
      {
        uri: `https://ipms.insollex.com/api/uploads/${file.filename}`,
        fileType: 'docx', // Specify the file type
      },
    ];
    console.log("Stepping");

    return (
      <Box sx={{ width: '100%', height: '60vh' }}>
        <DocViewer
          documents={docs}
          pluginRenderers={DocViewerRenderers}
          style={{ height: '100%', width: '100%' }} // Add this!
          config={{
            header: {
              disableHeader: true, // Optional: hide top bar if needed
            },
          }}
        />
      </Box>
    );
  } else if (fileExtension === 'xlsx') {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="h6">Excel File Preview (converted)</Typography>
        <ExcelFileViewer file={file} />
      </Box>
    );
  }

  return <Typography>No preview available for this file type</Typography>;
};
// Separate component to handle rendering of text files
const TextFileViewer = ({ file }) => {
  const [fileContent, setFileContent] = useState('');

  useEffect(() => {
    const fetchTextFile = async () => {
      try {
        const response = await fetch(`https://ipms.insollex.com/api/uploads/${file.filename}`);
        if (response.ok) {
          const text = await response.text();
          setFileContent(text);
        } else {
          console.error('Failed to fetch text file content');
        }
      } catch (error) {
        console.error('Error fetching text file:', error);
      }
    };

    fetchTextFile();
  }, [file.filename]);

  return <pre>{fileContent}</pre>;
};

// Excel File Viewer using SheetJS
const ExcelFileViewer = ({ file }) => {
  const [excelData, setExcelData] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(0);

  useEffect(() => {
    const fetchExcelFile = async () => {
      try {
        const response = await fetch(`https://ipms.insollex.com/api/uploads/${file.filename}`);
        if (response.ok) {
          const arrayBuffer = await response.arrayBuffer();
          const data = new Uint8Array(arrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          setSheetNames(workbook.SheetNames); // Save all sheet names
          const sheet = workbook.Sheets[workbook.SheetNames[selectedSheet]];
          const html = XLSX.utils.sheet_to_html(sheet);
          setExcelData(html);
        } else {
          console.error('Failed to fetch Excel file');
        }
      } catch (error) {
        console.error('Error fetching Excel file:', error);
      }
    };

    fetchExcelFile();
  }, [file.filename, selectedSheet]);

  return (
    <div>
      <select onChange={(e) => setSelectedSheet(Number(e.target.value))} value={selectedSheet}>
        {sheetNames.map((name, index) => (
          <option key={index} value={index}>{name}</option>
        ))}
      </select>

      <div
        dangerouslySetInnerHTML={{
          __html: excelData,
        }}
      />
    </div>
  );
};


const MatterDetailPage = () => {
  const { id } = useParams();
  const [matter, setMatter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState(0);  // For tab control
  const [isSaveAttempted, setIsSaveAttempted] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isEditingMode, setIsEditingMode] = useState(true);
  const hiddenFileInput = useRef(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [estateNo] = useState('');
  const [estateId, setEstateId] = useState(null);
  const { legalMatterId } = useParams();  // if you're using React Router's useParams
  const [oldMatter, setOldMatter] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState(null);
  const { search } = useLocation();
  const token = new URLSearchParams(search).get('token');
  const [isInvitedUser, setIsInvitedUser] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const theme = useTheme();
  const [user, setUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const [assetType, setAssetType] = useState('');
  const [isAssetsHovered, setIsAssetsHovered] = useState(false);
  const assetsTabRef = React.useRef(null);
  const [dropdownPosition, setDropdownPosition] = React.useState({ top: 0, left: 0 });
  const containerRef = React.useRef(null);
  const [showAssetDropdown, setShowAssetDropdown] = useState(false);


  const isDarkMode = theme.palette.mode === 'dark';
  const [updatedMatter, setUpdatedMatter] = useState({
    matterName: '',
    description: '',
    status: '',
    client: '',
    caseNumber: '',
    estateNo: '',
    magistrateOffice: '',
    mastersOffice: '',
    trusteeReference: '',
    estateCapturedBy: '',
    estateClassification: '',
    contributionDanger: '',
    referral: '',
    provisionalLiquidationDate: '',
    finalLiquidationDate: '',
    reductionRequestDate: '',
    reductionAllowedDate: '',
    dischargeDate: '',
    registrationNo: '',
    vatNo: '',
    taxNo: '',
    bankName: '',
    bankAccountNo: '',
    provisionalCertificate: false,
    provisionalCertificateApplication: false,
    insolventContactPhone: '',
    insolventContactEmail: '',

    // Trustee Info (First Name, Last Name, Telephone, Email)
    trustee1FirstName: '',
    trustee1LastName: '',
    trustee1Telephone: '',
    trustee1Email: '',

    trustee2FirstName: '',
    trustee2LastName: '',
    trustee2Telephone: '',
    trustee2Email: '',

    trustee3FirstName: '',
    trustee3LastName: '',
    trustee3Telephone: '',
    trustee3Email: '',

    trustee4FirstName: '',
    trustee4LastName: '',
    trustee4Telephone: '',
    trustee4Email: '',

    trustee5FirstName: '',
    trustee5LastName: '',
    trustee5Telephone: '',
    trustee5Email: '',

    trustee6FirstName: '',
    trustee6LastName: '',
    trustee6Telephone: '',
    trustee6Email: '',

    trustee7FirstName: '',
    trustee7LastName: '',
    trustee7Telephone: '',
    trustee7Email: '',

    trustee8FirstName: '',
    trustee8LastName: '',
    trustee8Telephone: '',
    trustee8Email: '',

    // Cashbook & Financial
    cashbookBalance: '',
    cashbookTotalDEP: '',
    cashbookTotalEXP: '',
    cashbookTotalDVO: '',
    cashbookTotalEVO: '',
    cashbookTotalDBA: '',
    cashbookTotalEBA: '',
    cashbookTotalDEC: '',
    cashbookTotalSDY: '',
    cashbookTotalDEB: '',
    cashbookTotalBAP: '',
    cashbookTotalVSK: '',
    cashbookTotalRPD: '',
    cashbookTotalDRS: '',
    cashbookTotalPMT: '',
    cashbookTotalBDP: '',
    cashbookTotalINV: '',
    cashbookTotalBLN: '',

    // Bond Security
    bondSecurityNumber: '',
    bondSecurityAmount: '',

    // Insurance / Policy Info
    companyName: '',
    policyNumber: '',
    insuredAmount: '',
    finalInsuredAmount: '',
    policyRenewalDate: '',

    // Misc
    reminder: '',
    comment: '',

    // Files
    documents: [],
  });


  const [formData, setFormData] = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [estate, setEstate] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [openFolders, setOpenFolders] = useState({});
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [folderForUpload, setFolderForUpload] = useState(null);
  const [isPublic, setIsPublic] = React.useState(false);
  const [selectedIsPublic, setSelectedIsPublic] = useState(false);
  const MAX_TRUSTEES = 8;

  const containerStyle = {
    border: '2px solid',
    borderColor: isDarkMode ? '#555' : '#ccc',
    padding: 2,
    borderRadius: 2,
    marginBottom: 2,
    boxShadow: 2,
    backgroundColor: isDarkMode ? '#1e1e1e' : '#fff',
    color: isDarkMode ? '#f0f0f0' : '#000'
  };

  // Sample folders
  const [folderList, setFolderList] = useState([
    'Court Application',
    'Provisional Court Order',
    'Final Court Order',
    'Final Certificate',
    'Confirmation',
    'Provincal Order'
  ]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get('https://ipms.insollex.com/api/user', { withCredentials: true });

        // Defensive check
        if (typeof res.data !== 'object' || res.data === null) {
          console.error("Invalid user data received:", res.data);
          setUser(null);
          return;
        }

        // Optional: additional checks
        if (!res.data.role) {
          console.warn("User has no role defined. Treating as guest.");
          res.data.role = "guest";
        }

        setUser(res.data);
      } catch (err) {
        console.error('Could not fetch user', err);
        setUser(null);
      }
    };

    fetchUser();
  }, []);


  useEffect(() => {
    const fetchMatter = async () => {
      if (!id) {
        setError("Invalid ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        let data;

        if (token) {
          // Invite link fetch
          const response = await fetch(`https://ipms.insollex.com/api/invite/${token}`);
          setIsInvitedUser(true);

          if (!response.ok) throw new Error("Invite link is invalid or expired");

          const inviteData = await response.json();
          data = inviteData.matter; // extract matter
          setEstateId(data.estateId || null);

        } else {
          // Regular fetch
          const isNewMatter = checkIfNewMatter(id);
          const url = isNewMatter
            ? `https://ipms.insollex.com/api/legal-matter/${id}`
            : ""; // `https://ipms.insollex.com/api/old-database/estates/${id}`;

          if (!url) throw new Error("Old database API is commented out");
          const response = await fetch(url);
          if (!response.ok) throw new Error("Failed to fetch matter data");

          const result = await response.json();
          data = isNewMatter ? result.matter || result : mapOldDataToUpdatedMatter(result);

          if (!isNewMatter) {
            setOldMatter(data);
            setEstateId(data.estateId);
          }
        }

        // Ensure estateClassification exists
        const classification = data.estateClassification || "";

        // Set both matter state and formData for conditional rendering
        setMatter(data);
        setUpdatedMatter(data);
        setFormData(prev => ({
          ...prev,
          ...data,
          estateClassification: classification
        }));

      } catch (error) {
        console.error("Error fetching matter data:", error);
        setError(error.message || "Failed to fetch matter data.");
      } finally {
        setLoading(false);
      }
    };

    fetchMatter();
  }, [id, token]);





  // Function to check if the matter is new or old
  const checkIfNewMatter = (id) => {
    if (!id) return false;

    // Improved regular expression to match UUID format (including lowercase and uppercase letters, digits, and hyphens)
    const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

    // Return true if the ID matches the UUID format
    return uuidRegex.test(id);
  };

  const formatDate = (date) => {
    return date ? new Date(date).toISOString().split('T')[0] : '';
  };

  // Normalize estate classification
  const mapLegacyEstateType = (type = '') => {
    switch (type.toLowerCase().trim()) {
      case 'p':
        return 'Person';
      case 'c':
        return 'Company';
      case 'b':
        return 'CC/BK';
      default:
        return type?.trim(); // In case already normalized
    }
  };

  const mapOldDataToUpdatedMatter = (oldData = {}) => {
    const estateId = oldData.ID || null;

    return {
      matterName: oldData.Name1?.trim() || '',
      matterName2: oldData.Name2?.trim() || '',
      matterName3: oldData.Name3?.trim() || '',
      initials: oldData.Initials?.trim() || '',
      firstName: oldData.FirstName1?.trim() || '',
      lastName: oldData.FirstName2?.trim() || '',
      description: oldData.Comments?.trim() || '',
      status: oldData.Status?.trim() || '',
      client: oldData.Client?.trim() || '',
      caseNumber: oldData.CaseNumber?.trim() || '',
      estateNo: oldData.EstateNo?.trim() || '',
      magistrateOffice: oldData.MagistrateOffice?.trim() || '',
      mastersOffice: oldData.MasterOffice?.trim() || '',
      trusteeReference: oldData.TrusteeReference?.trim() || '',
      estateCapturedBy: oldData.EstateCapturedBy?.trim() || '',

      // ✅ Normalize legacy estate classification
      estateClassification: mapLegacyEstateType(oldData.Type),

      contributionDanger: oldData.ContributionDanger || '0',
      referral: oldData.Referral?.trim() || '',

      // 🔹 Fixed Date Formatting 🔹
      provisionalLiquidationDate: formatDate(oldData.ProvLiqDate),
      finalLiquidationDate: formatDate(oldData.FinalLiqDate),
      provisionalAppointmentDate: formatDate(oldData.ProvAptDate),
      finalAppointmentDate: formatDate(oldData.FinalAptDate),
      reductionRequestDate: formatDate(oldData.ReducReqDate),
      reductionAllowedDate: formatDate(oldData.ReducAllowDate),
      dischargeDate: formatDate(oldData.DischargeDate),
      policyRenewalDate: formatDate(oldData.PolicyRenewalDate),

      registrationNo: oldData.RegistrationNo?.trim() || '',
      vatNo: oldData.VATNo?.trim() || '',
      taxNo: oldData.TaxNo?.trim() || '',
      bankName: oldData.BankName?.trim() || '',
      bankAccountNo: oldData.BankAccountNo?.trim() || '',

      // Trustee details
      trustee1Name: oldData.Trustee1?.trim() || '',
      trustee1Telephone: oldData.Trustee1Tel?.trim() || '',
      trustee1Email: oldData.Trustee1Email?.trim() || '',
      trustee2Name: oldData.Trustee2?.trim() || '',
      trustee2Telephone: oldData.Trustee2Tel?.trim() || '',
      trustee2Email: oldData.Trustee2Email?.trim() || '',
      trustee3Name: oldData.Trustee3?.trim() || '',
      trustee3Telephone: oldData.Trustee3Tel?.trim() || '',
      trustee3Email: oldData.Trustee3Email?.trim() || '',
      trustee4Name: oldData.Trustee4?.trim() || '',
      trustee4Telephone: oldData.Trustee4Tel?.trim() || '',
      trustee4Email: oldData.Trustee4Email?.trim() || '',

      // Bond & Insurance Details
      bondSecurityNumber: oldData.BondSecurityNumber?.trim() || '',
      bondSecurityAmount: oldData.BondSecurityAmount || '0.00',
      companyName: oldData.Insurer?.trim() || '',
      policyNumber: oldData.InsurerPolicyNumber?.trim() || '',
      insuredAmount: oldData.InsuredAmount || '0.00',
      finalInsuredAmount: oldData.FinalInsuredAmount || '0.00',
      policyRenewalDate: oldData.PolicyRenewalDate?.trim() || '',

      // Miscellaneous
      reminder: oldData.Reminder1?.trim() || '',
      comment: oldData.Comments?.trim() || '',
      documents: Array.isArray(oldData.Documents) ? oldData.Documents : [],

      estateId,
    };
  };

  // 🛠️ Updated State: Manages the number of trustee rows. Starts at 1.
  const [numTrustees, setNumTrustees] = useState(1);
    
  // Helper array to generate indices for mapping
  const trusteeIndices = Array.from({ length: numTrustees }, (_, i) => i + 1);

  const handleAddTrustee = () => {
      // Enforce the maximum limit of 8 trustees
      if (numTrustees < MAX_TRUSTEES) {
          setNumTrustees(prev => prev + 1);
      }
  };
  
  // Helper function to aggregate emails for the "Email All" button
  const getEmails = () => {
      const emails = [];
      for (let i = 1; i <= numTrustees; i++) {
          const email = formData[`trustee${i}Email`];
          if (email) {
              emails.push(email);
          }
      }
      return emails;
  };
  
  const isGuest = user?.role === 'guest' || user?.isInvited;



  const handleTabChange = (event, newValue) => setValue(newValue);

  // Enable editing mode
  const handleEditClick = () => setIsEditingMode(true);

  const handleSaveData = async () => {


    // Restrict if user is a guest or it's an invited view
    if (!user || user?.role === 'guest' || user.isInvited) {
      setSnackbarMessage("You are not allowed to save data.");
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (!id) {
      setSnackbarMessage("Error: Missing matter ID!");
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!updatedMatter.estateNo) {
      setSnackbarMessage("Error: Missing estate number!");
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setIsLoading(true); // Enable loading state

    try {
      const url = checkIfNewMatter(id)
        ? `https://ipms.insollex.com/api/legal-matter/${id}` // Saving for new matter (UUID)
        : ""; // `https://ipms.insollex.com/api/old-database/estates/${id}`; // Saving for old matter (numeric ID)

      if (!url) throw new Error("Old database API is commented out");
      // Debug: Log URL and updated data
      console.log("Saving data to:", url);
      console.log("Updated Data:", updatedMatter);

      // Send updated data to the server
      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedMatter), // Send updated matter data
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to save updates: ${errorText}`);
      }

      const data = await response.json();
      console.log("Data Returned from Server:", data);

      // Check if the response contains the expected 'updatedMatter' or just the message
      if (data.updatedMatter) {
        const updatedMatterFromServer = data.updatedMatter;

        // Debug: Check if data returned matches what we expect
        if (updatedMatterFromServer.estateNo !== updatedMatter.estateNo) {
          console.warn("Mismatch between sent data and returned data!");
          console.log("Sent data:", updatedMatter);
          console.log("Returned data:", updatedMatterFromServer);
        }

        // Update the state with the new data
        setFormData(prevState => ({
          ...prevState,
          ...updatedMatterFromServer,  // Merge with the updated data from the server
        }));
        setUpdatedMatter(updatedMatterFromServer);  // Set the updated matter
        setIsEditingMode(false);  // Disable editing mode
        setSnackbarMessage("Update successful!");
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        // If only a message is returned, show a success message
        setSnackbarMessage("Estate data updated successfully!");
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error saving updates:', error);
      setSnackbarMessage('Failed to save updates. Please check the console for details.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false); // Disable loading state
    }
  };

  const handleCashbookSummary = (summary) => {
    setFormData((prev) => ({ ...prev, ...summary }));
  };


  // Handle Snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => {
      if (prev[name] === value) return prev;
      return { ...prev, [name]: value };
    });

    setUpdatedMatter(prev => {
      if (prev[name] === value) return prev;
      return { ...prev, [name]: value };
    });
  };
  const emailAllTrustees = () => {
    const allEmails = Object.keys(formData)
      .filter(key => key.endsWith('Email') && formData[key]) // Only filled emails
      .map(key => formData[key]);

    if (allEmails.length === 0) {
      alert("No trustee emails provided.");
      return;
    }

    const mailtoLink = `mailto:${allEmails.join(',')}`;  // join multiple emails with commas
    window.location.href = mailtoLink; // opens default email client with all emails in To field
  };

  const emailSelectedTrustee = (trusteeIndex) => {
    const emailKey = `trustee${trusteeIndex}Email`;
    const selectedEmail = formData[emailKey];

    if (!selectedEmail) {
      alert(`Trustee ${trusteeIndex} does not have an email address.`);
      return;
    }

    const mailtoLink = `mailto:${selectedEmail}`;
    window.location.href = mailtoLink; // opens default email client with this email in To field
  };


  // Fetching documents logic - Automatically choose old or new database
  useEffect(() => {
  const fetchDocuments = async () => {
    if (!id) return;

    try {
      // Use the helper you already defined at line 416
      const isNew = checkIfNewMatter(id);
      
      // Determine path: New uses 'legal-matter', Old uses 'old-database/estates' (commented out)
      const basePath = isNew ? 'legal-matter' : ''; // 'old-database/estates';
      if (!basePath) return;
      const url = `https://ipms.insollex.com/api/${basePath}/${id}/documents`;

      console.log(`Fetching docs from: ${url}`); // Debugging is your friend here

      const response = await fetch(url);
      if (!response.ok) throw new Error(`Status: ${response.status}`);

      const documentsData = await response.json();
      setDocuments(documentsData);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  fetchDocuments();
}, [id]);



  // Function to determine if the document is from the old database or the new one
  const isOldDatabase = (id) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return !uuidRegex.test(String(id)); // Ensure ID is treated as a string
  };

  const handleFileUpload = async (folder = null, isPublic = false) => {
    if (selectedFiles.length === 0) {
      alert('Please select at least one file!');
      return;
    }
  
    setUploading(true);
    setUploadProgress(0);
  
    const formData = new FormData();
  
    // Append metadata
    formData.append('folder', folder || '');
    formData.append('isPublic', isPublic ? 'true' : 'false');
  
    // Append files correctly
    selectedFiles.forEach((item) => {
      const file = item instanceof File ? item : item.file;
      if (file instanceof File) {
        formData.append('documents', file, file.name); // <-- Ensure filename is passed
      }
    });
  
    // Debug FormData
    for (const [key, value] of formData.entries()) {
      console.log('FormData entry:', key, value, value instanceof File);
    }
  
    try {
      const url = `https://ipms.insollex.com/api/legal-matter/${id}/uploads`;
      const response = await axios.post(url, formData, {
        headers: {
          'x-user-email': user.emailAddress,
          'x-user-role': user.role,
          // Do NOT manually set Content-Type
        },
        onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / e.total)),
      });
  
      const newDocuments = response.data?.files || [];
  
      if (newDocuments.length > 0) {
        setDocuments((prev) =>
          prev.concat(newDocuments.map((doc) => ({ ...doc, folder, isPublic })))
        );
        setSnackbarMessage('Documents uploaded successfully!');
        setSnackbarSeverity('success');
      } else {
        setSnackbarMessage('Upload completed but no documents returned.');
        setSnackbarSeverity('warning');
      }
  
      setSnackbarOpen(true);
      setSelectedFiles([]);
      if (hiddenFileInput.current) hiddenFileInput.current.value = null;
    } catch (error) {
      console.error('Upload error:', error);
      setSnackbarMessage('Failed to upload documents.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };
  
  
  

  const toggleFolder = (folder) => {
    setOpenFolders(prev => ({ ...prev, [folder]: !prev[folder] }));
    setSelectedFolder(prev => (prev === folder ? null : folder));
  };



  // Trigger file input
  const handleButtonClick = () => {
    if (selectedFiles.length === 0) {
      // No files selected yet → open file picker
      hiddenFileInput.current.click();
    } else {
      // Files are selected → open folder selection dialog
      setFolderDialogOpen(true);
    }
  };


  // Select files, optionally assign them to the currently selected folder
  const handleFileChange = (event) => {
    const files = Array.from(event.target.files).map(file => ({
      file, // native File object
    }));

    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]); // accumulate files
    }
  };


  // Document Preview
  const handleOpenPreview = (file) => {
    setSelectedDocument(file);
    setOpenPreview(true);
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    setSelectedDocument(null);
  };

  // Delete Document - For both the new and old database
  const handleDeleteClick = (filename) => {
    setSelectedFile(filename);
    setOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (folderList.includes(selectedFile)) {
      setSnackbarMessage('Folders cannot be deleted.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      setOpen(false);
      return;
    }

    console.log(`Deleting document: ${selectedFile} for matter ID: ${id}`);
    try {
      const basePath = isOldDatabase(id) ? '' /*'old-database'*/ : 'legal-matter';
      if (!basePath) throw new Error("Old database API is commented out");
      const response = await fetch(`https://ipms.insollex.com/api/${basePath}/${id}/documents/${selectedFile}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete document: ${errorText}`);
      }

      // Remove deleted document from state
      setDocuments((prevDocs) => prevDocs.filter((file) => file.filename !== selectedFile));

      // Clear preview if the deleted file is being shown
      if (selectedDocument?.filename === selectedFile) {
        setSelectedDocument(null);
      }

      setOpen(false);
      setSnackbarMessage('Document deleted successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error deleting document:', error);
      setSnackbarMessage(error.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };


  const hoverTimeout = useRef(null);

  const handleAssetsMouseEnter = () => {
    if (assetsTabRef.current && containerRef.current) {
      const tabRect = assetsTabRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();

      setDropdownPosition({
        top: tabRect.bottom - containerRect.top,
        left: tabRect.left - containerRect.left,
      });
    }

    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }

    // Start timer to show dropdown after 3 seconds
    hoverTimeout.current = setTimeout(() => {
      setIsAssetsHovered(true);
    }, 3000);
  };

  const handleAssetsMouseLeave = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }
    // Wait a tiny bit before hiding to allow mouse to move to dropdown
    hoverTimeout.current = setTimeout(() => {
      setIsAssetsHovered(false);
    }, 200);
  };

  const handleDropdownMouseEnter = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }
    setIsAssetsHovered(true);
  };

  const handleDropdownMouseLeave = () => {
    setIsAssetsHovered(false);
  };

  const magistrateOffices = [
  // GAUTENG
  { label: "Johannesburg Magistrate Court", value: "johannesburg" },
  { label: "Pretoria Magistrate Court", value: "pretoria" },
  { label: "Randburg Magistrate Court", value: "randburg" },
  { label: "Soweto (Protea) Magistrate Court", value: "soweto" },
  { label: "Germiston Magistrate Court", value: "germiston" },
  { label: "Kempton Park Magistrate Court", value: "kempton_park" },
  { label: "Palm Ridge Magistrate Court", value: "palm_ridge" },
  { label: "Roodepoort Magistrate Court", value: "roodepoort" },
  { label: "Centurion Magistrate Court", value: "centurion" },

  // WESTERN CAPE
  { label: "Cape Town Magistrate Court", value: "cape_town" },
  { label: "Bellville Magistrate Court", value: "bellville" },
  { label: "Wynberg Magistrate Court", value: "wynberg" },
  { label: "Somerset West Magistrate Court", value: "somerset_west" },
  { label: "George Magistrate Court", value: "george" },
  { label: "Paarl Magistrate Court", value: "paarl" },

  // KWAZULU-NATAL
  { label: "Durban Magistrate Court", value: "durban" },
  { label: "Pietermaritzburg Magistrate Court", value: "pietermaritzburg" },
  { label: "Newcastle Magistrate Court", value: "newcastle" },
  { label: "Pinetown Magistrate Court", value: "pinetown" },
  { label: "Umlazi Magistrate Court", value: "umlazi" },
  { label: "Chatsworth Magistrate Court", value: "chatsworth" },

  // EASTERN CAPE
  { label: "Gqeberha (Port Elizabeth) Magistrate Court", value: "gqeberha" },
  { label: "East London Magistrate Court", value: "east_london" },
  { label: "Mthatha Magistrate Court", value: "mthatha" },
  { label: "Makhanda (Grahamstown) Magistrate Court", value: "makhanda" },
  { label: "Qonce (King Williams Town) Magistrate Court", value: "qonce" },

  // FREE STATE
  { label: "Bloemfontein Magistrate Court", value: "bloemfontein" },
  { label: "Welkom Magistrate Court", value: "welkom" },
  { label: "Sasolburg Magistrate Court", value: "sasolburg" },
  { label: "Kroonstad Magistrate Court", value: "kroonstad" },

  // LIMPOPO
  { label: "Polokwane Magistrate Court", value: "polokwane" },
  { label: "Thohoyandou Magistrate Court", value: "thohoyandou" },
  { label: "Tzaneen Magistrate Court", value: "tzaneen" },
  { label: "Mokopane Magistrate Court", value: "mokopane" },

  // MPUMALANGA
  { label: "Mbombela (Nelspruit) Magistrate Court", value: "mbombela" },
  { label: "Middelburg Magistrate Court", value: "middelburg" },
  { label: "Witbank (Emalahleni) Magistrate Court", value: "witbank" },
  { label: "Ermelo Magistrate Court", value: "ermelo" },

  // NORTH WEST
  { label: "Mahikeng Magistrate Court", value: "mahikeng" },
  { label: "Rustenburg Magistrate Court", value: "rustenburg" },
  { label: "Klerksdorp Magistrate Court", value: "klerksdorp" },
  { label: "Potchefstroom Magistrate Court", value: "potchefstroom" },

  // NORTHERN CAPE
  { label: "Kimberley Magistrate Court", value: "kimberley" },
  { label: "Upington Magistrate Court", value: "upington" },
  { label: "Kuruman Magistrate Court", value: "kuruman" },
  { label: "Springbok Magistrate Court", value: "springbok" }
];

  // Optional: cleanup on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout.current) {
        clearTimeout(hoverTimeout.current);
      }
    };
  }, []);

  return (
    <Box sx={{ padding: 2 }}>

      {/* Loading / Error States */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>Loading Matter Details...</Typography>
        </Box>
      ) : !matter && !oldMatter ? (
        <Typography variant="h6">Matter not found</Typography>
      ) : (
        <>

          {/* Matter/Estate name + EstateNo above the tabs */}
          {(matter?.matterName || estate?.EstateName || matter?.estateNo || estate?.estateNo) && (
            <Typography
              variant="h3" // larger than h6
              sx={{
                mb: 2,
                textAlign: 'center',   // center horizontally
                fontWeight: 'bold',    // make bold
              }}
            >
              {/* Name: prefer matter, fallback to estate */}
              Estate – {matter?.matterName || estate?.EstateName}

              {/* EstateNo: prefer matter, fallback to estate */}
              {(matter?.estateNo || estate?.estateNo) &&
                ` | Ref – ${matter?.estateNo || estate?.estateNo}`}
            </Typography>
          )}


          {/* Matter Tabs */}
          <Paper sx={{ mb: 2, position: 'relative' }} ref={containerRef}>
            <Tabs
              value={value}
              onChange={(e, newValue) => {
                setValue(newValue);
                if (newValue !== 4) setIsAssetsHovered(false);
              }}
              aria-label="Matter details tabs"
            >
              <Tab label="Estate Information" />
              <Tab label="Cashbook Transactions" />
              <Tab label="Accounts" />
              <Tab label="Meetings" />

              {/* Assets Tab */}
<Tab
  label="Assets"
  ref={assetsTabRef}
  onMouseEnter={() => setIsAssetsHovered(true)}
  onClick={() => setIsAssetsHovered((prev) => !prev)}
/>


              <Tab label="Documents" />
            </Tabs>

            {/* Dropdown stays the same */}
            {isAssetsHovered && assetsTabRef.current && (
              <ClickAwayListener onClickAway={() => setIsAssetsHovered(false)}>
                <Box
                  onMouseEnter={() => setIsAssetsHovered(true)}
                  onMouseLeave={() => setIsAssetsHovered(false)}
                  sx={{
                    position: 'absolute',
                    top: assetsTabRef.current.offsetTop + assetsTabRef.current.offsetHeight,
                    left: assetsTabRef.current.offsetLeft,
                    backgroundColor: 'background.paper',
                    boxShadow: 3,
                    p: 1,
                    width: 200,
                    zIndex: 10,
                    borderRadius: '0 0 4px 4px',
                    border: '1px solid rgba(0,0,0,0.12)',
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      setAssetType('encumbered');
                      setValue(4);
                      setIsAssetsHovered(false);
                    }}
                  >
                    Encumbered Assets
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setAssetType('free residue');
                      setValue(4);
                      setIsAssetsHovered(false);
                    }}
                  >
                    Free Residue Assets
                  </MenuItem>
                </Box>
              </ClickAwayListener>
            )}
          </Paper>

{value === 0 && (
  <Box>
    {/* Save/Update Button */}
    <Box sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1300 }}>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSaveData}
        sx={{ fontSize: '1rem', p: '10px 20px', boxShadow: 2, borderRadius: 2 }}
        disabled={isLoading || !formData?.estateNo || user?.role === 'guest' || user?.isInvited}
      >
        {isLoading ? 'Saving...' : formData?.id ? 'Update Estate' : 'Save Estate'}
      </Button>
    </Box>

    <Box sx={containerStyle}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Estate Information
      </Typography>

      <Grid container spacing={2}>
        {[
          { label: "Master Reference", name: "estateNo", type: "text" },
          { label: "Estate Name", name: "matterName", type: "text" },
          { 
            label: "Magistrate Office", 
            name: "magistrateOffice", 
            type: "select", 
            options: magistrateOffices.map(office => office.label) 
          },
          {
            label: "Masters Office",
            name: "mastersOffice",
            type: "select",
            options: [
              '(Bhisho) Eastern Cape', '(Bloemfontein) Free State', '(Cape Town) Western Cape',
              '(Durban) KwaZulu-Natal', '(Gqeberha) Eastern Cape', '(Johannesburg) Gauteng',
              '(Kimberley) Northern Cape', '(Makhanda) Eastern Cape', '(Mbombela) Mpumalanga',
              '(Mmabatho) North West', '(Middelburg) Mpumalanga', '(Pietermaritzburg) KwaZulu-Natal',
              '(Polokwane) Limpopo', '(Pretoria) Gauteng', '(Thohoyandou) Limpopo', '(Umtata) Eastern Cape',
            ]
          },
          { label: "Provisional Sequestration Date", name: "provisionalLiquidationDate", type: "date" },
          { label: "Final Sequestration Date", name: "finalLiquidationDate", type: "date" },
          { label: "Provisional Appointment Date", name: "provisionalAppointmentDate", type: "date" },
          { label: "Final Appointment Date", name: "finalAppointmentDate", type: "date" },
          { label: "Release Date", name: "dischargeDate", type: "date" },
        ].map(({ label, name, type, options }) => (
          <Grid item xs={12} sm={4} key={name}>
            {type === "select" ? (
              <TextField 
                select 
                fullWidth 
                label={label} 
                name={name} 
                value={formData[name] || ''} 
                onChange={handleFieldChange} 
                variant="outlined" 
                disabled={user?.role === 'guest' || user?.isInvited}
              >
                {options.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            ) : type === "date" ? (
              <TextField 
                fullWidth 
                label={label} 
                name={name} 
                type="date" 
                value={formData[name] || ''} 
                onChange={handleFieldChange} 
                variant="outlined" 
                disabled={user?.role === 'guest' || user?.isInvited} 
                InputLabelProps={{ shrink: true }} 
              />
            ) : (
              <TextField 
                fullWidth 
                label={label} 
                name={name} 
                value={formData[name] || ''} 
                onChange={handleFieldChange} 
                variant="outlined" 
                multiline 
                minRows={1} 
                maxRows={4} 
                disabled={user?.role === 'guest' || user?.isInvited} 
                InputLabelProps={{ shrink: !!formData[name] }} 
              />
            )}
          </Grid>
        ))}

                  {/* Conditionally Render Fields Based on Classification */}
                  {formData.estateClassification && (() => {
                    const classificationFields = {
                      Person: [
                        { label: "Initials", name: "initials" },
                        { label: "First Name", name: "firstName" },
                        { label: "Last Name", name: "lastName" },
                        { label: "ID Number", name: "idNumber" }
                      ],
                      Company: [
                        { label: "Registration No.", name: "registrationNo" },
                        { label: "VAT No.", name: "vatNo" },
                        { label: "Tax No.", name: "taxNo" }
                      ],
                      "CC/BK": [
                        { label: "Registration No.", name: "registrationNo" },
                        { label: "Tax No.", name: "taxNo" }
                      ]
                    };
                    return classificationFields[formData.estateClassification].map(({ label, name }) => (
                      <Grid item xs={12} sm={2} key={name}>
                        <TextField fullWidth label={label} name={name} value={formData[name] || ''} onChange={handleFieldChange} variant="outlined" multiline minRows={1} maxRows={4} disabled={user?.role === 'guest' || user?.isInvited} InputLabelProps={{ shrink: !!formData[name] }} />
                      </Grid>
                    ));
                  })()}

                  {/* Checkboxes at the end */}
                  {[
                    { label: "Provisional Certificate", name: "provisionalCertificate" },
                    { label: "Provisional Cert. Application", name: "provisionalCertificateApplication" },
                  ].map(({ label, name }) => (
                    <Grid item xs={12} sm={2} key={name}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!formData[name]}
                            onChange={(e) => handleFieldChange({ target: { name, value: e.target.checked } })}
                            name={name}
                            color="primary"
                            disabled={user?.role === 'guest' || user?.isInvited}
                          />
                        }
                        label={label}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>




              {/* Estate Bank Details Section */}
              <Box sx={containerStyle}>
                <Typography variant="h6" sx={{ marginBottom: 2 }}>Estate Bank Details</Typography>
                <Grid container spacing={2}>
                  {[
                    { label: 'Bank Name', name: 'bankName' },
                    { label: 'Bank Account No', name: 'bankAccountNo' }
                  ].map(({ label, name }) => (
                    <Grid item xs={12} sm={6} key={name}>
                      <TextField
                        fullWidth
                        label={label}
                        name={name}
                        value={formData[name] || ''}
                        onChange={handleFieldChange}
                        variant="outlined"
                        multiline
                        minRows={1}
                        maxRows={4}
                        disabled={user?.role === 'guest' || user?.isInvited}
                        InputLabelProps={{
                          shrink: formData[name] ? true : false,  // Labels stay inside when there's a value

                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Bond Security Information */}
              <Box sx={containerStyle}>
                <Typography variant="h6" sx={{ marginBottom: 2 }}>
                  Bond Security Information
                </Typography>

                <Grid container spacing={2}>

                  {/* Insurer Name */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Insurer Name"
                      name="insurerName"
                      value={formData.insurerName || ''}
                      onChange={handleFieldChange}
                      variant="outlined"
                      multiline
                      minRows={1}
                      maxRows={4}
                      disabled={user?.role === 'guest' || user?.isInvited}
                    />
                  </Grid>

                 {/* Insured Amount */}
<Grid item xs={12} sm={6}>
  <TextField
    fullWidth
    label="Insured Amount"
    name="insuredAmount"
    
    value={formData.insuredAmount || ''}
    
    onChange={handleFieldChange}
    variant="outlined"
    multiline
    minRows={1}
    maxRows={4}
    disabled={user?.role === 'guest' || user?.isInvited}

    InputProps={{
      startAdornment: <Typography variant="body1" sx={{ mr: 1 }}>R</Typography>,
    }}

    onBlur={(event) => {
      const rawValue = event.target.value;
      
      
      if (rawValue && !isNaN(parseFloat(rawValue))) {
       
        const formattedValue = parseFloat(rawValue).toFixed(2);

        handleFieldChange({
          target: {
            name: 'insuredAmount', 
            value: formattedValue,
          },
        });
      }
    }}
  />
</Grid>

                  {/* Policy Number */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Policy Number"
                      name="policyNumber"
                      value={formData.policyNumber || ''}
                      onChange={handleFieldChange}
                      variant="outlined"
                      multiline
                      minRows={1}
                      maxRows={4}
                      disabled={user?.role === 'guest' || user?.isInvited}
                    />
                  </Grid>

                  {/* Policy Renewal Date */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Policy Renewal Date"
                      name="policyRenewalDate"
                      type="date"
                      value={formData.policyRenewalDate || ''}
                      onChange={handleFieldChange}
                      variant="outlined"
                      disabled={user?.role === 'guest' || user?.isInvited}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        inputProps: { min: '1900-01-01', max: '2100-12-31' },
                      }}
                    />
                  </Grid>

                  {/* Reduction/Increase Date */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Reduction/Increase Date"
                      name="reductionIncreaseDate"
                      type="date"
                      value={formData.reductionIncreaseDate || ''}
                      onChange={handleFieldChange}
                      variant="outlined"
                      disabled={user?.role === 'guest' || user?.isInvited}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        inputProps: { min: '1900-01-01', max: '2100-12-31' },
                      }}
                    />
                  </Grid>

                 {/* Reduction/Increase Amount */}
<Grid item xs={12} sm={6}>
  <TextField
    fullWidth
    label="Reduction/Increase Amount"
    name="finalInsuredAmount"

    value={formData.finalInsuredAmount || ''}
    
    onChange={handleFieldChange}
    variant="outlined"
    multiline
    minRows={1}
    maxRows={4}
    disabled={user?.role === 'guest' || user?.isInvited}
    InputProps={{
      startAdornment: <Typography variant="body1" sx={{ mr: 1 }}>R</Typography>,
    }}
    
    onBlur={(event) => {
      const rawValue = event.target.value;
      if (rawValue && !isNaN(parseFloat(rawValue))) {
        
        const numberValue = parseFloat(rawValue);

        const formattedValue = numberValue.toFixed(2);

        handleFieldChange({
          target: {
            name: 'finalInsuredAmount',
            value: formattedValue,
          },
        });
      }
    }}
  />
</Grid>

                  {/* Inception Date
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label="Inception Date"
        name="inceptionDate"
        type="date"
        value={formData.inceptionDate || ''}
        onChange={handleFieldChange}
        variant="outlined"
        disabled={user?.role === 'guest' || user?.isInvited}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          inputProps: { min: '1900-01-01', max: '2100-12-31' },
        }}
      />
    </Grid> */}

                  {/* Bond Security Number
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label="Bond Security Number"
        name="bondSecurityNumber"
        value={formData.bondSecurityNumber || ''}
        onChange={handleFieldChange}
        variant="outlined"
        multiline
        minRows={1}
        maxRows={4}
        disabled={user?.role === 'guest' || user?.isInvited}
      />
    </Grid> */}

                  {/* Bond Security Amount
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label="Bond Security Amount"
        name="bondSecurityAmount"
        value={
          formData.bondSecurityAmount && !isNaN(formData.bondSecurityAmount)
            ? parseFloat(formData.bondSecurityAmount).toFixed(2)
            : ''
        }
        onChange={handleFieldChange}
        variant="outlined"
        multiline
        minRows={1}
        maxRows={4}
        disabled={user?.role === 'guest' || user?.isInvited}
      />
    </Grid> */}

                  {/* Inception Amount
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label="Inception Amount"
        name="inceptionAmount"
        value={
          formData.inceptionAmount && !isNaN(formData.inceptionAmount)
            ? parseFloat(formData.inceptionAmount).toFixed(2)
            : ''
        }
        onChange={handleFieldChange}
        variant="outlined"
        multiline
        minRows={1}
        maxRows={4}
        disabled={user?.role === 'guest' || user?.isInvited}
      />
    </Grid> */}

                  {/* Reduction Request Date
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label="Reduction Request Date"
        name="reductionRequestDate"
        type="date"
        value={formData.reductionRequestDate || ''}
        onChange={handleFieldChange}
        variant="outlined"
        disabled={user?.role === 'guest' || user?.isInvited}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          inputProps: { min: '1900-01-01', max: '2100-12-31' },
        }}
      />
    </Grid> */}

                  {/* Reduction Allowed Date
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label="Reduction Allowed Date"
        name="reductionAllowedDate"
        type="date"
        value={formData.reductionAllowedDate || ''}
        onChange={handleFieldChange}
        variant="outlined"
        disabled={user?.role === 'guest' || user?.isInvited}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          inputProps: { min: '1900-01-01', max: '2100-12-31' },
        }}
      />
    </Grid> */}

                </Grid>
              </Box>


              <Box sx={containerStyle}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ marginBottom: 2 }}>
                <Typography variant="h6">Appointed Trustees</Typography>
                
                {/* 🛠️ BUTTON UPDATED TO DISABLE AT MAX_TRUSTEES */}
                <Tooltip title={numTrustees >= MAX_TRUSTEES ? `Maximum ${MAX_TRUSTEES} trustees reached` : "Add Trustee Row"}>
                    <IconButton 
                        color="primary" 
                        onClick={handleAddTrustee} 
                        disabled={numTrustees >= MAX_TRUSTEES || isGuest}
                    >
                        <AddCircleOutlineIcon />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Grid container spacing={2}>
                {/* MAPPING OVER DYNAMIC INDICES */}
                {trusteeIndices.map(i => {
                    const trusteeNameField = `trustee${i}FullName`;
                    const trusteeEmailField = `trustee${i}Email`;
                    const trusteePhoneField = `trustee${i}Telephone`;

                    return (
                        <React.Fragment key={i}>
                            {/* Trustee Name (Combined Field) */}
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label={`Trustee ${i} Name`}
                                    name={trusteeNameField}
                                    value={formData[trusteeNameField] || ''}
                                    onChange={handleFieldChange}
                                    variant="outlined"
                                    disabled={isGuest}
                                />
                            </Grid>

                            {/* Telephone */}
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    fullWidth
                                    label={`Trustee ${i} Telephone`}
                                    name={trusteePhoneField}
                                    value={formData[trusteePhoneField] || ''}
                                    onChange={handleFieldChange}
                                    variant="outlined"
                                    disabled={isGuest}
                                />
                            </Grid>

                            {/* Email with Envelope Button */}
                            <Grid item xs={12} sm={5}>
                                <TextField
                                    fullWidth
                                    label={`Trustee ${i} Email`}
                                    name={trusteeEmailField}
                                    value={formData[trusteeEmailField] || ''}
                                    onChange={handleFieldChange}
                                    variant="outlined"
                                    disabled={isGuest}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label={`Email Trustee ${i}`}
                                                    onClick={() => {
                                                        const email = formData[trusteeEmailField];
                                                        if (email) {
                                                            window.location.href = `mailto:${email}`;
                                                        }
                                                    }}
                                                    edge="end"
                                                    disabled={isGuest || !formData[trusteeEmailField]}
                                                >
                                                    <MailOutlineIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        </React.Fragment>
                    );
                })}
            </Grid>

            {/* Email All button */}
            <Box sx={{ marginTop: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={isGuest}
                    onClick={() => {
                        const emails = getEmails();
                        if (emails.length) {
                            window.location.href = `mailto:${emails.join(';')}`;
                        }
                    }}
                >
                    Email All Trustees ({numTrustees})
                </Button>
            </Box>
        </Box>

              {/* Comments Section */}
              <Box sx={{ ...containerStyle, marginTop: 4 }}>
                <Typography variant="h6" sx={{ marginBottom: 2 }}>Comments</Typography>
                <TextField
                  fullWidth
                  label="Comments"
                  name="comment"
                  value={formData.comment || ''}
                  onChange={handleFieldChange}
                  variant="outlined"
                  multiline
                  minRows={3}
                  maxRows={8}
                  disabled={user?.role === 'guest' || user?.isInvited}
                />
              </Box>
            </Box>
          )}
          {/* Snackbar Component */}
          <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
            <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>

          {value === 1 && <CashbookTransactionsTab matter={matter} estateId={estateId} onSummaryUpdate={handleCashbookSummary} />}
          {value === 2 && <AccountsTab matter={matter} estateId={estateId} />}
          {value === 3 && <AppointmentsTab matter={matter} estateId={estateId} />}
          {value === 4 && assetType && (
            <AssetsTab matter={matter} estateId={estateId} assetType={assetType} />
          )}

          {value === 4 && !assetType && !isAssetsHovered && (
            <Box sx={{ p: 2 }}>Please select an asset type to view assets.</Box>
          )}

          {value === 5 && (
            <Box>
              <Typography variant="h6" align="center" sx={{ color: 'text.primary' }}>
                Documents
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                <Box
                  sx={{
                    width: { xs: '100%', md: '30%' },
                    paddingRight: { md: 2 },
                    overflowY: 'auto',
                    marginBottom: { xs: 2, md: 0 },
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: 2,
                  }}
                >
                  <List>
                    {/* Folders */}
                    {folderList.map((folder) => (
                      <Box key={folder}>
                        <ListItem
                          button
                          onClick={() => toggleFolder(folder)}
                          sx={{
                            marginBottom: 1,
                            borderRadius: 1,
                            backgroundColor: selectedFolder === folder ? '#d0e0ff' : '#f5f5f5',
                            fontWeight: 'bold',
                          }}
                        >
                          <Typography sx={{ flexGrow: 1 }}>📁 {folder}</Typography>
                          {openFolders[folder] ? <ExpandLess /> : <ExpandMore />}
                        </ListItem>

                        {/* Files inside folder */}
                        <Collapse in={openFolders[folder]} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {documents
                              .filter((file) => file.folder === folder)
                              .map((file) => (
                                <ListItem
                                  key={file.filename}
                                  sx={{
                                    pl: 4,
                                    mb: 1,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    '&:hover': { boxShadow: 1 },
                                  }}
                                >
                                  <Tooltip title={file.filename} arrow>
                                    <Typography
                                      sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flexGrow: 1,
                                      }}
                                      onClick={() => handleOpenPreview(file)}
                                    >
                                      {file.filename}
                                    </Typography>
                                  </Tooltip>

                                  <IconButton
                                    edge="end"
                                    aria-label="delete"
                                    onClick={() => handleDeleteClick(file.filename)}
                                    sx={{ color: 'red' }}
                                    disabled={user?.role === 'guest' || user?.isInvited}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </ListItem>
                              ))}
                          </List>
                        </Collapse>
                      </Box>
                    ))}

                    {/* Files not in a folder */}
                    {documents
                      .filter((file) => !file.folder)
                      .map((file) => (
                        <ListItem
                          key={file.filename}
                          sx={{
                            marginBottom: 1,
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            '&:hover': { boxShadow: 2 },
                            paddingLeft: 2,
                          }}
                        >
                          <Tooltip title={file.filename} arrow>
                            <Typography
                              variant="body1"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flexGrow: 1,
                              }}
                              onClick={() => handleOpenPreview(file)}
                            >
                              {file.filename}
                            </Typography>
                          </Tooltip>

                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => handleDeleteClick(file.filename)}
                            sx={{ color: 'red' }}
                            disabled={user?.role === 'guest' || user?.isInvited}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItem>
                      ))}
                  </List>

                  {/* Delete Confirmation Dialog */}
                  <Dialog open={open} onClose={() => setOpen(false)}>
                    <DialogTitle>Confirm Deletion</DialogTitle>
                    <DialogContent>
                      <DialogContentText>
                        Are you sure you want to delete <strong>{selectedFile}</strong>? This
                        action cannot be undone.
                      </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setOpen(false)} color="primary">
                        Cancel
                      </Button>
                      <Button onClick={handleConfirmDelete} color="error" disabled={user?.role === 'guest' || user?.isInvited}>
                        Delete
                      </Button>
                    </DialogActions>

                  </Dialog>
                  <Dialog open={folderDialogOpen} onClose={() => setFolderDialogOpen(false)}>
                    <DialogTitle>Select Folder & Visibility</DialogTitle>
                    <DialogContent>
                      <DialogContentText>
                        Choose a folder to upload the selected file(s) into.
                        Optionally mark the file(s) as public.
                      </DialogContentText>

                      {/* Folder Selection */}
                      <List>
                        {folderList.map(folder => (
                          <ListItem
                            button
                            key={folder}
                            selected={folderForUpload === folder}
                            onClick={() => setFolderForUpload(folder)}
                          >
                            <Typography>{folder}</Typography>
                          </ListItem>
                        ))}
                        <ListItem
                          button
                          selected={folderForUpload === null}
                          onClick={() => setFolderForUpload(null)}
                        >
                          <Typography>No Folder</Typography>
                        </ListItem>
                      </List>

                      {/* Public Checkbox */}
                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          id="publicCheckbox"
                          checked={isPublic}
                          onChange={(e) => setIsPublic(e.target.checked)}
                          style={{ marginRight: 8 }}
                        />
                        <label htmlFor="publicCheckbox">Make this document public</label>
                      </Box>
                    </DialogContent>

                    <DialogActions>
                      <Button onClick={() => setFolderDialogOpen(false)} color="primary">
                        Cancel
                      </Button>
                      <Button
                        onClick={() => {
                          setFolderDialogOpen(false);
                          handleFileUpload(folderForUpload, isPublic); // pass folder + public flag
                          setFolderForUpload(null);
                          setIsPublic(false); // reset after upload
                        }}
                        color="primary"
                        disabled={selectedFiles.length === 0}
                      >
                        Upload
                      </Button>
                    </DialogActions>
                  </Dialog>

                  {/* Hidden file input */}
                  <input
                    type="file"
                    accept=".pdf,.docx,.jpg,.jpeg,.png,.txt,.xlsx,.pptx"
                    onChange={handleFileChange}
                    multiple
                    style={{ display: 'none' }}
                    ref={hiddenFileInput}
                  />

                  {/* Upload Button */}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleButtonClick}
                    disabled={uploading}
                  >
                    {selectedFiles.length > 0
                      ? `Upload ${selectedFiles.length} File${selectedFiles.length > 1 ? 's' : ''}`
                      : 'Choose Documents'}
                  </Button>

                  {selectedFiles.length > 0 && (
                    <Typography variant="caption" sx={{ mt: 1 }}>
                      Selected: {selectedFiles.map(f => f.file.name).join(', ')}
                    </Typography>
                  )}

                  {/* Progress Bar */}
                  {uploading && (
                    <Box sx={{ width: '100%', mt: 1 }}>
                      <LinearProgress variant="determinate" value={uploadProgress} />
                      <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'right' }}>
                        Uploading: {uploadProgress}%
                      </Typography>
                    </Box>
                  )}

                  {/* Optional loading text or spinner */}
                  {uploading && (
                    <Typography variant="body2" sx={{ marginLeft: 2 }}>
                      Uploading: {selectedFiles[0]?.file.name} {/* show first file being uploaded */}
                    </Typography>
                  )}
                </Box>

                {/* Right Side - Document Preview */}
                <Box sx={{ width: { xs: '100%', md: '70%' }, paddingLeft: { md: 2 } }}>
                  {selectedDocument ? (
                    <Box>{renderDocumentPreview(selectedDocument)}</Box>
                  ) : (
                    <Typography variant="h6">Select a document to preview</Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {/* Snackbar Component */}
          <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
            <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </>
      )}
    </Box>
  );
}

export default MatterDetailPage;